import React, { FC } from 'react';
import type { GroupBase, StylesConfig } from 'react-select';

import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

//components
import SearchableSelect, {
    GroupedOption,
    SearchableSelectType,
} from '../../inputs/searchableSelect';
import SingleValue from '../../inputs/selectSingleValue';
import SingleValueCompact from '../../inputs/selectSingleValueCompact';

const shouldForwardProps = createShouldForwardProp(['isBorderless']);
interface SingleSelectFilterProps {
    options: SearchableSelectType[] | GroupedOption[];
    value?: SearchableSelectType[] | SearchableSelectType;
    onChange: (
        field: string,
        value: SearchableSelectType[] | SearchableSelectType,
    ) => void;
    name: string;
    label: string;
    defaultValue?: SearchableSelectType[] | SearchableSelectType;
    isCompact?: boolean;
    isBorderless?: boolean;
    isRightBorder?: boolean;
    placeholder?: string;
    menuPortalTarget?: HTMLElement | null;
    menuAlign?: 'left' | 'right';
}

type SelectStyles = StylesConfig<
    SearchableSelectType,
    boolean,
    GroupBase<SearchableSelectType>
>;

const customStyles: SelectStyles = {
    // Only used when menuPortalTarget is set; sits above drawers and modals.
    menuPortal: (provided) => ({ ...provided, zIndex: 3000 }),
    control: (provided) => ({
        ...provided,
        boxShadow: 'none',
        border: 'none',
        boxSizing: 'border-box',
        minHeight: '34px',
        display: 'flex',
        alignItems: 'center',
        padding: aampeTheme.spacing4(0, 2, 0, 2),
        flexWrap: 'nowrap',
    }),
    valueContainer: (provided) => ({
        ...provided,
        display: 'flex',
        padding: 0,
        cursor: 'pointer',
        flexWrap: 'nowrap',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: aampeTheme.spacing4(0, 1),
    }),
    menu: (provided) => ({
        ...provided,
        maxWidth: '288px',
        minWidth: '150px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
        padding: aampeTheme.spacing4(2),
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? aampeTheme.colors.backgroundWG02
            : aampeTheme.colors.white,
        color: aampeTheme.colors.black,
        '&:hover': {
            backgroundColor: aampeTheme.colors.interactiveWG01,
        },
        padding: aampeTheme.spacing4(2),
    }),
};

/**
 * 'right' anchors the menu to the control's right edge so it opens leftwards,
 * for filters at the right edge of the page where a wider menu would overflow.
 */
export const getSingleSelectStyles = (
    menuAlign: 'left' | 'right' = 'left',
): SelectStyles =>
    menuAlign === 'right'
        ? {
              ...customStyles,
              menu: (provided, state) => ({
                  ...customStyles.menu!(provided, state),
                  right: 0,
                  left: 'auto',
              }),
          }
        : customStyles;

const SingleSelectFilter: FC<SingleSelectFilterProps> = ({
    options,
    value,
    name,
    onChange,
    label,
    defaultValue,
    isCompact,
    isBorderless,
    isRightBorder,
    placeholder,
    menuPortalTarget,
    menuAlign,
}) => {
    return (
        <Container isBorderless={isBorderless}>
            <StyledSearchableSelect
                components={{
                    SingleValue: (innerProps: any) =>
                        isCompact ? (
                            <SingleValueCompact {...innerProps} title={label} />
                        ) : (
                            <SingleValue {...innerProps} title={label} />
                        ),
                }}
                name={name}
                options={options}
                isCompact={isCompact}
                onChange={onChange}
                defaultValue={defaultValue}
                layout="horizontal"
                customStyles={getSingleSelectStyles(menuAlign)}
                value={value}
                isSearchable={false}
                addRightBorder={isRightBorder}
                labelStyle={{
                    fontWeight: aampeTheme.weights.light,
                    fontSize: aampeTheme.size.font.default,
                    lineHeight: aampeTheme.size.lineHeight.default,
                    flexWrap: 'nowrap',
                }}
                placeholder={placeholder}
                menuPortalTarget={menuPortalTarget}
            />
        </Container>
    );
};

const Container = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<{ isBorderless?: boolean }>(({ isBorderless }) => ({
    border: isBorderless
        ? 'none'
        : `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: isBorderless ? 'none' : aampeTheme.borderRadius.default,
    minWidth: 'max-content',
    height: '36px',
    boxSizing: 'border-box',
}));

const StyledSearchableSelect = styled(SearchableSelect)({
    height: '36px',
});

export default SingleSelectFilter;
